'use client';

import { createDebug } from '@shared/debug';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FieldPath, type FieldValues, type UseFormReturn, useFormContext } from 'react-hook-form';
import type { MultistepFormContextType, MultistepFormProps } from '../types';

const debug = createDebug({ isEnabled: true });

/**
 * Type definition for a component with validateAndUpdateData static method
 */
type StepComponentWithValidation = {
  type: {
    validateAndUpdateData?: (formMethods: ReturnType<typeof useFormContext>) => Promise<boolean>;
  };
  props: Record<string, unknown>;
};

/**
 * Headless hook for managing multistep form state and logic.
 * Provides all the functionality without any presentational markup.
 * Use this when you need full control over your form's layout and styling.
 *
 * This hook is form-agnostic and doesn't require React Hook Form.
 * If you're using React Hook Form, wrap your component with FormProvider.
 * If you're not using forms, the hook will work without validation.
 *
 * @param steps - Array of step definitions
 * @param onComplete - Callback when form is completed
 * @param onStepChange - Callback when step changes
 * @param onMultistepFormReady - Callback when multistep form is ready with skipToStep and setStatus functions
 * @param formMethods - Optional React Hook Form methods. Pass when FormProvider is not an ancestor (e.g. custom dialog layout).
 * @returns Multistep form state and navigation methods
 *
 * @example
 * ```tsx
 * // Without React Hook Form (no validation)
 * const formState = useMultistepFormState({
 *   steps,
 *   onComplete: handleComplete,
 * });
 *
 * return (
 *   <MultistepFormProvider value={formState}>
 *     <div className="your-custom-layout">
 *       {formState.currentStep?.component}
 *     </div>
 *   </MultistepFormProvider>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // With React Hook Form (with validation)
 * const form = useForm();
 * const formState = useMultistepFormState({
 *   steps,
 *   onComplete: handleComplete,
 * });
 *
 * return (
 *   <FormProvider {...form}>
 *     <MultistepFormProvider value={formState}>
 *       <div className="your-custom-layout">
 *         {formState.currentStep?.component}
 *       </div>
 *     </MultistepFormProvider>
 *   </FormProvider>
 * );
 * ```
 */
type UseMultistepFormStateOptions<T extends FieldValues = FieldValues> = Pick<MultistepFormProps, 'steps' | 'onComplete' | 'onStepChange' | 'onMultistepFormReady'> & {
  /** Pass when FormProvider is not an ancestor. Enables validation without context. */
  formMethods?: UseFormReturn<T>;
};

function useMultistepFormState<T extends FieldValues = FieldValues>({
  steps,
  onComplete,
  onStepChange,
  onMultistepFormReady,
  formMethods: formMethodsProp,
}: UseMultistepFormStateOptions<T>) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [formErrors, setFormErrors] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('');
  const [pendingStepId, setPendingStepId] = useState<string | null>(null);
  const previousStepIndexRef = useRef<number>(0);
  const isNavigatingBackwardsRef = useRef<boolean>(false);

  let formMethodsFromContext: ReturnType<typeof useFormContext> | undefined;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    formMethodsFromContext = useFormContext();
  } catch {
    formMethodsFromContext = undefined;
  }

  const formMethods = (formMethodsProp ?? formMethodsFromContext) as UseFormReturn<T> | undefined;

  // Filter steps based on conditions - memoized to prevent unnecessary recalculations
  const visibleSteps = useMemo(() => steps.filter(step => !step.condition || step.condition({ formData, status })), [steps, formData, status]);

  // Ensure currentStepIndex is valid when visibleSteps changes
  useEffect(() => {
    // If currentStepIndex is out of bounds or the current step is no longer visible
    if (currentStepIndex >= visibleSteps.length || !visibleSteps[currentStepIndex]) {
      // Find the closest valid step index
      let newIndex = 0;
      if (visibleSteps.length > 0) {
        // Try to find the step that was at the current index in the original steps array
        const currentStepId = steps[currentStepIndex]?.id;
        if (currentStepId) {
          const foundIndex = visibleSteps.findIndex(step => step.id === currentStepId);
          if (foundIndex !== -1) {
            newIndex = foundIndex;
          } else {
            // If the current step is not found, go to the last valid step or 0
            newIndex = Math.min(currentStepIndex, visibleSteps.length - 1);
          }
        }
      }

      if (newIndex !== currentStepIndex) {
        setCurrentStepIndex(newIndex);
      }
    }
  }, [visibleSteps, currentStepIndex, steps]);

  // Notify when step changes
  useEffect(() => {
    const stepId = visibleSteps[currentStepIndex]?.id;
    if (!stepId) return;
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === visibleSteps.length - 1;
    onStepChange?.(isFirstStep, isLastStep, stepId);
  }, [currentStepIndex, visibleSteps, onStepChange]);

  // Update the previous step index ref whenever currentStepIndex changes
  useEffect(() => {
    previousStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  // Effect to handle step navigation when status changes
  useEffect(() => {
    if (pendingStepId) {
      const stepIndex = visibleSteps.findIndex(step => step.id === pendingStepId);
      if (stepIndex !== -1) {
        // Clear any existing form errors when navigating
        formMethods?.clearErrors();
        setCurrentStepIndex(stepIndex);
        setFormErrors(false);
      }
      setPendingStepId(null);
    }
  }, [pendingStepId, visibleSteps, formMethods]);

  // Effect to reset the backwards navigation flag after navigation
  useEffect(() => {
    if (isNavigatingBackwardsRef.current) {
      // Reset the flag on the next tick to allow the auto-navigation logic to run
      const timeoutId = setTimeout(() => {
        isNavigatingBackwardsRef.current = false;
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [currentStepIndex]);

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  // Ensure currentStep is defined
  const currentStep = visibleSteps[currentStepIndex];

  // Extract validation logic for testability
  const isValidStepIndex = (index: number, steps: typeof visibleSteps): boolean => {
    return index >= 0 && index < steps.length && !!steps[index];
  };

  // Helper function to safely navigate to a step index
  const navigateToStep = (newIndex: number, options?: { isBackwards?: boolean }) => {
    if (isValidStepIndex(newIndex, visibleSteps)) {
      if (options?.isBackwards) {
        isNavigatingBackwardsRef.current = true;
      }
      setCurrentStepIndex(newIndex);
      setFormErrors(false);
      return true;
    }
    return false;
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      const newIndex = currentStepIndex - 1;
      navigateToStep(newIndex, { isBackwards: true });
    }
  };

  const goToNextStep = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setFormErrors(false);
      isNavigatingBackwardsRef.current = false;

      // Get the current step component safely
      const currentStep = visibleSteps[currentStepIndex];
      const currentStepComponent = currentStep?.component as StepComponentWithValidation;

      // If React Hook Form is not present, skip validation and just navigate
      if (!formMethods) {
        if (isLastStep) {
          onComplete?.(formData);
        } else {
          const newIndex = currentStepIndex + 1;
          navigateToStep(newIndex);
        }
        return;
      }

      // Only perform validation if React Hook Form is present
      // Check if the step component has a validateAndUpdateData static method
      if (currentStepComponent.type && typeof currentStepComponent.type.validateAndUpdateData === 'function') {
        const isValid = await currentStepComponent.type.validateAndUpdateData(formMethods as UseFormReturn<FieldValues>);
        if (!isValid) {
          setFormErrors(true);
          return;
        }
      } else {
        const currentStepId = currentStep?.id;
        const isValid = await formMethods.trigger(currentStepId as FieldPath<T>);
        if (!isValid && currentStepId) {
          setFormErrors(true);
          // trigger()'s shouldFocus runs before React re-renders, so focus is a no-op on
          // first validation. setFocus() uses setTimeout, running after the render cycle.
          formMethods.setFocus(currentStepId as FieldPath<T>);
          return;
        }
      }

      if (isLastStep) {
        // Last step fields have forwardRef, so shouldFocus works correctly here.
        const isFormValid = await formMethods.trigger(undefined, { shouldFocus: true });
        if (isFormValid) {
          onComplete?.(formData);
        } else {
          setFormErrors(true);
        }
      } else {
        const newIndex = currentStepIndex + 1;
        navigateToStep(newIndex);
      }
    } catch (error) {
      debug.forceError('Error in goToNextStep:', error);
      setFormErrors(true);
    } finally {
      setIsLoading(false);
    }
  };

  const goToStep = (index: number) => {
    navigateToStep(index);
  };

  const skipToStep = useCallback(
    (stepId: string) => {
      // Clear any existing form errors when skipping steps (if React Hook Form is present)
      formMethods?.clearErrors();
      // Set the pending step ID, which will be processed in the useEffect
      isNavigatingBackwardsRef.current = false;
      setPendingStepId(stepId);
    },
    [formMethods]
  );

  const updateFormData = (stepId: string, data: unknown) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: data,
    }));
  };

  // Effect to call onMultistepFormReady callback with skipToStep and setStatus functions
  useEffect(() => {
    if (onMultistepFormReady) {
      onMultistepFormReady(skipToStep, setStatus);
    }
  }, [onMultistepFormReady, skipToStep]);

  const contextValue: MultistepFormContextType = {
    currentStepIndex,
    steps: visibleSteps,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    skipToStep,
    isLoading,
    setIsLoading,
    formData,
    updateFormData,
    formErrors,
    setStatus,
    status,
  };

  return {
    ...contextValue,
    currentStep,
  };
}

export { useMultistepFormState };
